import React, { useId } from "react";
import { useField } from "formik";
import SearchableSelect from "./SearchableSelect";

const showError = (meta) => Boolean(meta.touched && meta.error);

/** Label, helper text and inline error around any control. */
export const FieldShell = ({ id, label, required, helper, error, className = "", children }) => (
  <div className={`fk-field ${className}`}>
    {label && (
      <label className="fk-label" htmlFor={id}>
        {label}
        {required && (
          <span className="fk-req" aria-hidden="true">
            *
          </span>
        )}
      </label>
    )}
    {children}
    {error ? (
      <div className="fk-error" id={`${id}-error`} role="alert">
        {error}
      </div>
    ) : (
      helper && (
        <div className="fk-help" id={`${id}-help`}>
          {helper}
        </div>
      )
    )}
  </div>
);

const describedBy = (id, error, helper) => (error ? `${id}-error` : helper ? `${id}-help` : undefined);

export const TextField = ({
  name,
  label,
  required,
  helper,
  className,
  type = "text",
  prefix,
  ...inputProps
}) => {
  const [field, meta] = useField(name);
  const error = showError(meta) ? meta.error : null;
  const input = (
    <input
      {...field}
      {...inputProps}
      id={name}
      type={type}
      className="fk-input"
      value={field.value ?? ""}
      aria-invalid={error ? "true" : undefined}
      aria-required={required ? "true" : undefined}
      aria-describedby={describedBy(name, error, helper)}
    />
  );
  return (
    <FieldShell id={name} label={label} required={required} helper={helper} error={error} className={className}>
      {prefix ? (
        <div className="fk-affix">
          <span className="fk-affix__pre">{prefix}</span>
          {input}
        </div>
      ) : (
        input
      )}
    </FieldShell>
  );
};

export const TextAreaField = ({ name, label, required, helper, className, maxLength, rows = 5, ...inputProps }) => {
  const [field, meta] = useField(name);
  const error = showError(meta) ? meta.error : null;
  const length = String(field.value ?? "").length;
  return (
    <FieldShell
      id={name}
      label={label}
      required={required}
      helper={helper}
      error={error}
      className={className}
    >
      <textarea
        {...field}
        {...inputProps}
        id={name}
        rows={rows}
        className="fk-input"
        value={field.value ?? ""}
        aria-invalid={error ? "true" : undefined}
        aria-required={required ? "true" : undefined}
        aria-describedby={describedBy(name, error, helper)}
      />
      {maxLength && (
        <div className={`fk-counter ${length > maxLength ? "fk-counter--over" : ""}`}>
          <span>
            {length}/{maxLength}
          </span>
        </div>
      )}
    </FieldShell>
  );
};

// Indian digit grouping for the visible value; the stored value stays a plain number string
const formatIndian = (raw) => {
  if (raw === "" || raw == null) return "";
  const [intPart, decPart] = String(raw).split(".");
  let formatted = intPart;
  if (intPart.length > 3) {
    const rest = intPart.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ",");
    formatted = `${rest},${intPart.slice(-3)}`;
  }
  return decPart !== undefined ? `${formatted}.${decPart}` : formatted;
};

const compactIndian = (raw) => {
  const n = Number(raw);
  if (!n || Number.isNaN(n)) return "";
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2).replace(/\.?0+$/, "")} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2).replace(/\.?0+$/, "")} Lac`;
  if (n >= 1e3) return `₹${(n / 1e3).toFixed(2).replace(/\.?0+$/, "")} K`;
  return "";
};

export const CurrencyField = ({ name, label, required, helper, className, placeholder }) => {
  const [field, meta, helpers] = useField(name);
  const error = showError(meta) ? meta.error : null;
  const hint = compactIndian(field.value);
  return (
    <FieldShell
      id={name}
      label={label}
      required={required}
      helper={hint ? `${hint}${helper ? ` · ${helper}` : ""}` : helper}
      error={error}
      className={className}
    >
      <div className="fk-affix">
        <span className="fk-affix__pre">₹</span>
        <input
          id={name}
          name={name}
          type="text"
          inputMode="decimal"
          className="fk-input"
          placeholder={placeholder}
          value={formatIndian(field.value)}
          onChange={(e) => {
            const cleaned = e.target.value.replace(/[^\d.]/g, "");
            const parts = cleaned.split(".");
            helpers.setValue(parts.length > 1 ? `${parts[0]}.${parts.slice(1).join("")}` : cleaned);
          }}
          onBlur={() => helpers.setTouched(true)}
          aria-invalid={error ? "true" : undefined}
          aria-required={required ? "true" : undefined}
          aria-describedby={describedBy(name, error, hint || helper)}
        />
      </div>
    </FieldShell>
  );
};

/** Searchable select bound to a Formik field. onSelect(value, option) can set related fields. */
export const SelectField = ({
  name,
  label,
  required,
  helper,
  className,
  options,
  placeholder,
  disabled,
  onSelect,
}) => {
  const [field, meta, helpers] = useField(name);
  const error = showError(meta) ? meta.error : null;
  return (
    <FieldShell id={name} label={label} required={required} helper={helper} error={error} className={className}>
      <SearchableSelect
        id={name}
        options={options}
        value={field.value}
        placeholder={placeholder || `Select ${label ? label.toLowerCase() : ""}`}
        disabled={disabled}
        invalid={Boolean(error)}
        describedBy={describedBy(name, error, helper)}
        onChange={(value, option) => {
          helpers.setValue(value);
          if (onSelect) onSelect(value, option);
        }}
        onBlur={() => helpers.setTouched(true)}
      />
    </FieldShell>
  );
};

/** Pill toggle group for short option sets. options: [{ value, label }] (values may be booleans/numbers). */
export const SegmentField = ({ name, label, required, helper, className, options, onSelect }) => {
  const [field, meta, helpers] = useField(name);
  const error = showError(meta) ? meta.error : null;
  return (
    <FieldShell id={name} label={label} required={required} helper={helper} error={error} className={className}>
      <div
        className="fk-seg"
        role="radiogroup"
        id={name}
        aria-label={label}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={describedBy(name, error, helper)}
      >
        {options.map((o) => (
          <button
            key={String(o.value)}
            type="button"
            role="radio"
            className="fk-seg__btn"
            aria-checked={field.value === o.value}
            onClick={() => {
              helpers.setValue(o.value);
              helpers.setTouched(true, false);
              if (onSelect) onSelect(o.value, o);
            }}
          >
            {o.label}
          </button>
        ))}
      </div>
    </FieldShell>
  );
};

/** Toggle chips. selected: array of option values. */
export const ChipSelect = ({ options, selected, onToggle, ariaLabel }) => (
  <div className="fk-chips" role="group" aria-label={ariaLabel}>
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        className="fk-chip"
        aria-pressed={selected.includes(o.value)}
        onClick={() => onToggle(o.value)}
      >
        {o.label}
      </button>
    ))}
  </div>
);

// A div (not <section>): the app has a global `section { padding: 80px 0 }` rule
export const Section = ({ title, hint, children }) => {
  const headingId = useId();
  return (
    <div className="fk-section" role="group" aria-labelledby={headingId}>
      <div className="fk-section__head">
        <h2 className="fk-section__title" id={headingId}>
          {title}
        </h2>
        {hint && <p className="fk-section__hint">{hint}</p>}
      </div>
      {children}
    </div>
  );
};
